import { useState, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react';
import { useEditorState, type Editor } from '@tiptap/react';
import {
  Bold, Italic, Underline, Strikethrough, Heading1, Heading2, Heading3, List, ListOrdered, Quote, Minus,
  TextAlignEnd, TextAlignCenter, TextAlignStart, TextAlignJustify, Link, Unlink, Table, Undo2, Redo2,
  BetweenHorizontalEnd, BetweenVerticalEnd, Rows3, Columns3, Trash2, RemoveFormatting,
} from 'lucide-react';
import { isSafeHref } from '@agio/core/domain';
import { ADVISOR_COLORS } from './advisorExtensions.js';
import { toolbarState } from './toolbarState.js';
import { cn } from '../../utils.js';

/**
 * Hebrew toolbar for the advisor-summary editor. Wraps onto more lines on a
 * phone instead of scrolling sideways. Every button has a Hebrew name (shown
 * on hover and read by screen readers).
 */
export function EditorToolbar({ editor }: { editor: Editor | null }) {
  const [linkOpen, setLinkOpen] = useState(false);
  const s = useEditorState({
    editor,
    // Re-run on every transaction — typing, clicking, focus and blur all are one.
    selector: ({ editor: e }) => (e ? toolbarState(e, e.isFocused) : null),
  });
  if (!editor || !s) return null;
  const chain = () => editor.chain().focus();

  return (
    <div className="border-b border-input bg-muted/50">
      <div role="toolbar" aria-label="עיצוב הטקסט" className="flex flex-wrap items-center gap-0.5 p-1.5">
        <Btn label="מודגש" active={s.bold} onClick={() => chain().toggleBold().run()}><Bold /></Btn>
        <Btn label="נטוי" active={s.italic} onClick={() => chain().toggleItalic().run()}><Italic /></Btn>
        <Btn label="קו תחתון" active={s.underline} onClick={() => chain().toggleUnderline().run()}><Underline /></Btn>
        <Btn label="קו חוצה" active={s.strike} onClick={() => chain().toggleStrike().run()}><Strikethrough /></Btn>
        <Sep />
        <Btn label="כותרת ראשית" active={s.h1} onClick={() => chain().toggleHeading({ level: 1 }).run()}><Heading1 /></Btn>
        <Btn label="כותרת משנה" active={s.h2} onClick={() => chain().toggleHeading({ level: 2 }).run()}><Heading2 /></Btn>
        <Btn label="כותרת קטנה" active={s.h3} onClick={() => chain().toggleHeading({ level: 3 }).run()}><Heading3 /></Btn>
        <Sep />
        <Btn label="רשימת תבליטים" active={s.bullet} onClick={() => chain().toggleBulletList().run()}><List /></Btn>
        <Btn label="רשימה ממוספרת" active={s.ordered} onClick={() => chain().toggleOrderedList().run()}><ListOrdered /></Btn>
        <Btn label="ציטוט" active={s.quote} onClick={() => chain().toggleBlockquote().run()}><Quote /></Btn>
        <Btn label="קו מפריד" onClick={() => chain().setHorizontalRule().run()}><Minus /></Btn>
        <Sep />
        <Btn label="יישור לימין" active={s.right} onClick={() => chain().setTextAlign('right').run()}><TextAlignEnd /></Btn>
        <Btn label="מרכוז" active={s.center} onClick={() => chain().setTextAlign('center').run()}><TextAlignCenter /></Btn>
        <Btn label="יישור לשמאל" active={s.left} onClick={() => chain().setTextAlign('left').run()}><TextAlignStart /></Btn>
        <Btn label="יישור לשני הצדדים" active={s.justify} onClick={() => chain().setTextAlign('justify').run()}><TextAlignJustify /></Btn>
        <Sep />
        <div className="flex items-center gap-1 px-1" role="group" aria-label="צבע טקסט">
          {ADVISOR_COLORS.map((c) => (
            <button
              key={c.value}
              type="button"
              title={`צבע ${c.label}`}
              aria-label={`צבע ${c.label}`}
              aria-pressed={s.color === c.value}
              onMouseDown={keepFocus}
              onClick={() => chain().setColor(c.value).run()}
              className={cn(
                'advisor-swatch h-6 w-6 rounded-full border-2 transition-transform hover:scale-110',
                s.color === c.value ? 'border-foreground' : 'border-transparent',
              )}
              data-color={c.value}
            />
          ))}
          <Btn label="ללא צבע" onClick={() => chain().unsetColor().run()}><RemoveFormatting /></Btn>
        </div>
        <Sep />
        <Btn label={s.link ? 'עריכת קישור' : 'הוספת קישור'} active={s.link || linkOpen} onClick={() => setLinkOpen((o) => !o)}><Link /></Btn>
        {s.link && <Btn label="הסרת קישור" onClick={() => chain().extendMarkRange('link').unsetLink().run()}><Unlink /></Btn>}
        <Sep />
        <Btn label="הוספת טבלה" onClick={() => chain().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}><Table /></Btn>
        {s.inTable && (
          <>
            <Btn label="הוספת שורה" onClick={() => chain().addRowAfter().run()}><BetweenHorizontalEnd /></Btn>
            <Btn label="הוספת עמודה" onClick={() => chain().addColumnAfter().run()}><BetweenVerticalEnd /></Btn>
            <Btn label="מחיקת שורה" onClick={() => chain().deleteRow().run()}><Rows3 /></Btn>
            <Btn label="מחיקת עמודה" onClick={() => chain().deleteColumn().run()}><Columns3 /></Btn>
            <Btn label="מחיקת הטבלה" onClick={() => chain().deleteTable().run()}><Trash2 /></Btn>
          </>
        )}
        <Sep />
        <Btn label="ביטול פעולה" disabled={!s.canUndo} onClick={() => chain().undo().run()}><Undo2 /></Btn>
        <Btn label="חזרה על פעולה" disabled={!s.canRedo} onClick={() => chain().redo().run()}><Redo2 /></Btn>
      </div>
      {linkOpen && (
        <LinkForm
          initial={s.linkHref}
          onCancel={() => setLinkOpen(false)}
          onSave={(href) => {
            chain().extendMarkRange('link').setLink({ href }).run();
            setLinkOpen(false);
          }}
        />
      )}
    </div>
  );
}

function LinkForm({ initial, onSave, onCancel }: { initial: string; onSave: (href: string) => void; onCancel: () => void }) {
  const [href, setHref] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const submit = () => {
    const v = href.trim();
    // A bare address ("www.example.com") is taken as https.
    const full = /^[a-z][a-z0-9+.-]*:/i.test(v) ? v : `https://${v}`;
    if (!isSafeHref(full)) {
      setError('אפשר לקשר רק לכתובת אינטרנט (http / https) או לדוא"ל (mailto)');
      return;
    }
    onSave(full);
  };
  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-input p-2 text-sm">
      <label className="flex min-w-0 flex-1 items-center gap-2">
        <span className="shrink-0 text-muted-foreground">כתובת הקישור</span>
        <input
          type="url"
          dir="ltr"
          autoFocus
          value={href}
          placeholder="https://"
          onChange={(e) => { setHref(e.target.value); setError(null); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); submit(); }
            if (e.key === 'Escape') onCancel();
          }}
          className="min-w-0 flex-1 rounded-md border border-input bg-popover px-2 py-1"
        />
      </label>
      <button type="button" onClick={submit} className="rounded-md bg-primary px-3 py-1 font-semibold text-primary-foreground">
        שמירת קישור
      </button>
      <button type="button" onClick={onCancel} className="rounded-md border border-border px-3 py-1 hover:bg-muted">
        ביטול
      </button>
      {error && <p role="alert" className="w-full text-destructive">{error}</p>}
    </div>
  );
}

function Btn({
  label, active, disabled, onClick, children,
}: { label: string; active?: boolean; disabled?: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      onMouseDown={keepFocus}
      onClick={onClick}
      className={cn(
        'grid h-8 w-8 place-items-center rounded-md text-foreground transition-colors [&_svg]:h-4 [&_svg]:w-4',
        'hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40',
        active && 'bg-primary text-primary-foreground hover:bg-primary',
      )}
    >
      {children}
    </button>
  );
}

/** Pressing a toolbar control must not take focus (and with it the cursor) away from the text. */
function keepFocus(e: ReactMouseEvent) {
  e.preventDefault();
}

function Sep() {
  return <span aria-hidden className="mx-1 h-5 w-px bg-border" />;
}
